import { useCallback, useEffect, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ElementRenderer } from "./element-renderer"
import { DropdownMenu } from "../components/ui/dropdown-menu"
import type { CogoElement } from "../core/types"
import { readResolvedArray, recordValue, resolvedText, shorthandId } from "./property-value-utils"

function coerceOpen(value: unknown): boolean {
  if (typeof value === "boolean") return value
  return String(value ?? "").trim().toLowerCase() === "true"
}

function shorthandChildren(
  element: RendererProps["element"],
  resolveSymbol: (value: string) => unknown,
  highlightedIndex: number,
): CogoElement[] {
  if (element.items === undefined) return element.children ?? []
  const items = readResolvedArray(element, "items", resolveSymbol)
  const contentChildren = items.map((raw, index) => {
    const item = recordValue(raw)
    const type = item.type === "separator" ? "dropdown-menu-separator" : "dropdown-menu-item"
    return {
      ...item,
      id: String(item.id ?? shorthandId(element.id, "items", index)),
      type,
      text: item.label ?? item.text,
      disabled: item.disabled,
      className: index === highlightedIndex
        ? `${String(item.className ?? "")} bg-accent text-accent-foreground`.trim()
        : item.className,
    } as unknown as CogoElement
  })
  const existingTrigger = element.children?.find((child) => child.type === "dropdown-menu-trigger")
  const existingContent = element.children?.find((child) => child.type === "dropdown-menu-content")
  return [
    existingTrigger ?? {
      id: `${element.id}__items_trigger`,
      type: "dropdown-menu-trigger",
      text: resolvedText(element.label ?? element.text, resolveSymbol, "Menu"),
    },
    {
      ...existingContent,
      id: `${element.id}__items_content`,
      type: "dropdown-menu-content",
      children: contentChildren,
    },
  ]
}

/**
 * DropdownMenuRenderer — base-ui DropdownMenu.Root provider.
 *
 * Children should include dropdown-menu-trigger (and its inner icon/button)
 * + dropdown-menu-content (with dropdown-menu-item children).
 *
 * Open state options:
 *  - Uncontrolled: omit `bind` / `open` / `defaultOpen` → Root manages internally
 *  - Bound: element.bind = "#{ui.menuOpen}" → state-controlled via ActionFlow
 */
export function DropdownMenuRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: DropdownMenu root is a base-ui provider that
  // doesn't render a DOM element of its own, so we surface the atom
  // contract on a thin wrapping span. The portal'd trigger/content inside
  // continue to render normally.
  const atomDefaults = useAtomDefaults("dropdown")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const [bound, setBound] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    coerceOpen((element as { defaultOpen?: unknown }).defaultOpen),
    coerceOpen,
  )
  const defaultOpenSource = (element as { defaultOpen?: unknown }).defaultOpen
  const resolvedDefaultOpen = typeof defaultOpenSource === "string"
    ? resolveSymbol(defaultOpenSource)
    : defaultOpenSource
  const defaultOpen = coerceOpen(resolvedDefaultOpen)
  const explicitOpen = (element as { open?: unknown }).open
  const resolvedExplicitOpen = typeof explicitOpen === "string"
    ? resolveSymbol(explicitOpen)
    : explicitOpen
  const requestedUnboundOpen = explicitOpen !== undefined
    ? coerceOpen(resolvedExplicitOpen)
    : defaultOpen
  const [unboundOpen, setUnboundOpen] = useState(requestedUnboundOpen)
  const highlightedIndex = Number((element as unknown as { highlightedIndex?: unknown }).highlightedIndex ?? -1)
  const isOpen = element.bind !== undefined ? bound : unboundOpen

  useEffect(() => {
    if (element.bind === undefined && (explicitOpen !== undefined || element.defaultOpen !== undefined)) {
      setUnboundOpen(requestedUnboundOpen)
    }
  }, [element.bind, element.defaultOpen, explicitOpen, requestedUnboundOpen])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (element.bind !== undefined) setBound(open)
      else setUnboundOpen(open)
      dispatchEvent(element.id, "openChange", { open })
    },
    [element.id, element.bind, setBound, dispatchEvent],
  )

  const rootProps: Record<string, unknown> = {
    open: isOpen,
    onOpenChange: handleOpenChange,
  }
  const children = shorthandChildren(element, resolveSymbol, highlightedIndex)
  const resolvedItems = element.items === undefined
    ? undefined
    : readResolvedArray(element, "items", resolveSymbol)

  return (
    <span
      data-atom-type="dropdown"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-is-open={isOpen ? "true" : "false"}
      data-highlighted-index={Number.isFinite(highlightedIndex) ? highlightedIndex : -1}
      data-value={isOpen ? "true" : "false"}
      data-items={resolvedItems === undefined ? undefined : JSON.stringify(resolvedItems)}
      data-testid={`dropdown-${element.id}`}
      aria-expanded={isOpen}
      className={element.className ? `inline-block ${element.className}` : undefined}
    >
      <DropdownMenu {...rootProps}>
        {children.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </DropdownMenu>
    </span>
  )
}
